import { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from "react";
import type { CartItem, SelectedModifier, UserData, ApplicableDiscount, SechUser, TaxCalculationResult } from "@/types/pos";
import type { SelectablePaymentMethod } from "@/types/payment-methods";
import { useToast } from "@/hooks/use-toast";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { apiFetch, getFirebaseAuthToken } from "@/utils";
import PosConfirmModal from "@/components/pos/confirmation-modal";

function areModifiersEqual(
	mods1: SelectedModifier[] | null | undefined,
	mods2: SelectedModifier[] | null | undefined
): boolean {
	const cartMods = mods1 || [];
	const itemMods = mods2 || [];
	if (cartMods.length !== itemMods.length) return false;
	const sortedCartMods = [...cartMods].sort((a, b) => a.modifierId - b.modifierId);
	const sortedItemMods = [...itemMods].sort((a, b) => a.modifierId - b.modifierId);
	return sortedCartMods.every((mod, idx) => mod.modifierId === sortedItemMods[idx].modifierId);
}

interface CartContextType {
	cartItems: CartItem[];
	addToCart: (item: CartItem) => void;
	removeFromCart: (itemId: string) => void;
	updateQuantity: (itemId: string, quantity: number) => void;
	clearCart: () => void;
	getCartTotal: () => number;
	getCartItemCount: () => number;
	sechUser: SechUser | null;
	requestPayment: (orderId?: string, userData?: UserData, notes?: string, paymentMethod?: SelectablePaymentMethod) => void;
	isPaymentProcessing: boolean;
	taxCalculation: TaxCalculationResult | null;
	isCalculatingTaxes: boolean;
	appliedDiscount: ApplicableDiscount | null;
	promoCode: string;
	setPromoCode: (code: string) => void;
	applyPromoCode: (paymentMethod?: string) => Promise<void>;
	removeDiscount: () => void;
	isValidatingPromo: boolean;
	promoError: string | null;
	getDiscountedTotal: () => number;
	refreshDiscount: (paymentMethod?: string) => void;
	lastPaymentError: string | null;
	clearLastPaymentError: () => void;
	/** True when a charge succeeded but the /v1/pos/orders record failed and can be retried. */
	pendingOrderRecord: boolean;
	/** True while a retry of the order record (no re-charge) is in flight. */
	isRecoveringOrder: boolean;
	/** Re-POST the already-charged order to /v1/pos/orders WITHOUT charging again. */
	retryOrderRecord: () => Promise<void>;
}

interface CartProviderProps {
	children: ReactNode;
	sechUser?: SechUser | null;
	executePayment: (paymentData: any) => Promise<{ success: boolean; txHash?: string; orderId?: string; error?: string }>;
	onPaymentSuccess?: (order: any) => void;
	onCartChange?: (hasItems: boolean) => void;
	location?: { id: number; name?: string; details?: string } | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function PosCartProvider({
	children,
	sechUser = null,
	executePayment,
	onPaymentSuccess,
	onCartChange,
	location
}: CartProviderProps) {
	const [cartItems, setCartItems] = useState<CartItem[]>([]);
	const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
	const [taxCalculation, setTaxCalculation] = useState<TaxCalculationResult | null>(null);
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false);
	const [cartLocation, setCartLocation] = useState<string | null>(null);
	const [pending, setPending] = useState<CartItem | null>(null);

	const [appliedDiscount, setAppliedDiscount] = useState<ApplicableDiscount | null>(null);
	const [promoCode, setPromoCode] = useState("");
	const [isValidatingPromo, setIsValidatingPromo] = useState(false);
	const [promoError, setPromoError] = useState<string | null>(null);
	const [manualPromoApplied, setManualPromoApplied] = useState(false);
	const [autoApplyDismissed, setAutoApplyDismissed] = useState(false);
	const [lastPaymentError, setLastPaymentError] = useState<string | null>(null);
	const clearLastPaymentError = useCallback(() => setLastPaymentError(null), []);

	const { toast } = useToast();
	const { sechPrice } = useSechPrice();

	// Notify parent about cart state changes
	useEffect(() => {
		onCartChange?.(cartItems.length > 0);
	}, [cartItems.length, onCartChange]);

	const userRef = useRef<UserData | null>(null);
	const notesRef = useRef<string | null>(null);
	// Synchronous re-entrancy lock for checkout. `isPaymentProcessing` is React state,
	// so it only disables the Pay button on the next render — two rapid taps can both
	// pass the isPaymentProcessing guard before that render lands and fire two charges,
	// which MobiusPay then rejects as a "Duplicate transaction". A ref flips immediately
	// (before the first await), so the second tap is blocked synchronously.
	const isSubmittingRef = useRef(false);

	// Recovery for a charge that succeeded but whose /v1/pos/orders record failed.
	// We stash everything needed to re-POST the order so the customer can retry the
	// record-only step WITHOUT being charged a second time.
	const recoveryRef = useRef<{
		orderData: any;
		items: CartItem[];
		taxCalc: TaxCalculationResult | null;
		discount: ApplicableDiscount | null;
		paymentMethod?: SelectablePaymentMethod;
	} | null>(null);
	const [pendingOrderRecord, setPendingOrderRecord] = useState(false);
	const [isRecoveringOrder, setIsRecoveringOrder] = useState(false);



	const addToCart = (item: CartItem) => {
		if (cartLocation && cartLocation !== item?.itemLocation) {
			setPending(item);
			return;
		}
		if (!cartLocation) {
			setCartLocation(item?.itemLocation);
		}
		setCartItems(prev => {
			const existingItemIndex = prev.findIndex(cartItem => {
				if (item.type === "product" && cartItem.type === "product") {
					if (cartItem.productId !== item.productId) return false;
					return areModifiersEqual(cartItem.selectedModifiers, item.selectedModifiers);
				}
				return false;
			});

			if (existingItemIndex >= 0) {
				const updated = [...prev];
				updated[existingItemIndex] = {
					...updated[existingItemIndex],
					quantity: updated[existingItemIndex].quantity + item.quantity,
				};
				return updated;
			}
			return [...prev, item];
		});
		toast({
			title: "Added to cart",
			description: `${item.product?.name || item.customDrink?.name} added to your cart.`,
		});
	};

	const removeFromCart = (itemId: string) => {
		setCartItems(prev => prev.filter(item => item.id !== itemId));
		toast({
			title: "Removed from cart",
			description: "Item has been removed from your cart.",
		});
	};

	const updateQuantity = (itemId: string, quantity: number) => {
		if (quantity <= 0) {
			removeFromCart(itemId);
			return;
		}
		setCartItems(prev =>
			prev.map(item => item.id === itemId ? { ...item, quantity } : item)
		);
	};

	const clearCart = () => {
		setCartItems([]);
		setTaxCalculation(null);
		setCartLocation(null);
		setAppliedDiscount(null);
		setPromoCode("");
		setPromoError(null);
		setManualPromoApplied(false);
		setAutoApplyDismissed(false);
	};

	const getCartTotal = () => {
		return cartItems.reduce((total, item) => total + (item.unitPriceUsd * item.quantity), 0);
	};

	const getCartTotalWithTaxes = () => {
		return taxCalculation ? taxCalculation.total : getCartTotal();
	};

	const getCartItemCount = () => {
		return cartItems.reduce((count, item) => count + item.quantity, 0);
	};

	const getDiscountedTotal = () => {
		const subtotal = getCartTotal();
		if (appliedDiscount) {
			return Math.max(0, subtotal - appliedDiscount.calculatedAmount);
		}
		return subtotal;
	};

	const calculateDiscount = useCallback(async (items: CartItem[], subtotal: number, manualCode?: string, paymentMethod?: string) => {
		if (items.length === 0 || subtotal <= 0 || !location?.id) {
			setAppliedDiscount(null);
			return;
		}

		try {
			const body: Record<string, any> = {
				items: items.map(item => ({
					productId: item.productId,
					productCategory: item.product?.category,
					productSubcategoryId: item.product?.subcategory?.id,
					locationId: location.id,
					unitPriceUsd: item.unitPriceUsd,
					quantity: item.quantity,
				})),
				locationId: location.id,
				cartSubtotal: subtotal,
				customerId: sechUser?.uid,
				promoCode: manualCode || undefined,
			};
			if (paymentMethod) body.paymentMethod = paymentMethod;

			const response = await apiFetch('/v1/pos/discounts/validate', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(body),
			});

			let result;
			try {
				result = await response.json();
			} catch {
				return;
			}

			if (!response.ok) return;

			if (result.bestDiscount) {
				setAppliedDiscount(result.bestDiscount);
			} else if (!manualCode) {
				setAppliedDiscount(null);
			}
		} catch (error) {
			console.error('Failed to calculate discounts:', error);
		}
	}, [location?.id, sechUser?.uid]);

	const applyPromoCode = async (paymentMethod?: string) => {
		if (!promoCode.trim()) {
			setPromoError("Please enter a promo code");
			return;
		}

		setIsValidatingPromo(true);
		setPromoError(null);

		try {
			const subtotal = getCartTotal();
			const body: Record<string, any> = {
				items: cartItems.map(item => ({
					productId: item.productId,
					productCategory: item.product?.category,
					productSubcategoryId: item.product?.subcategory?.id,
					locationId: location?.id,
					unitPriceUsd: item.unitPriceUsd,
					quantity: item.quantity,
				})),
				locationId: location?.id,
				cartSubtotal: subtotal,
				customerId: sechUser?.uid,
				promoCode: promoCode.trim(),
			};
			if (paymentMethod) body.paymentMethod = paymentMethod;

			const response = await apiFetch('/v1/pos/discounts/validate', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(body),
			});

			let result;
			try {
				result = await response.json();
			} catch {
				setPromoError("Server error - please try again");
				return;
			}

			if (!response.ok || !result.bestDiscount) {
				setPromoError(result.message || "Invalid or expired promo code");
				return;
			}

			const newDiscount = result.bestDiscount;

			if (newDiscount.promoCode !== promoCode.trim().toUpperCase()) {
				setPromoError("Invalid or expired promo code");
				return;
			}

			if (appliedDiscount && appliedDiscount.calculatedAmount >= newDiscount.calculatedAmount) {
				setPromoError(`Your current discount saves you more ($${appliedDiscount.calculatedAmount.toFixed(2)} vs $${newDiscount.calculatedAmount.toFixed(2)})`);
				return;
			}

			setAppliedDiscount(newDiscount);
			setManualPromoApplied(true);
			setAutoApplyDismissed(false);
			setPromoError(null);

			toast({
				title: "Promo code applied!",
				description: `${newDiscount.discountName} - You save $${newDiscount.calculatedAmount.toFixed(2)}`,
			});
		} catch {
			setPromoError("Failed to validate promo code");
		} finally {
			setIsValidatingPromo(false);
		}
	};

	const removeDiscount = () => {
		if (appliedDiscount && appliedDiscount.isAutoApply && !manualPromoApplied) {
			setAutoApplyDismissed(true);
		}
		setAppliedDiscount(null);
		setPromoCode("");
		setPromoError(null);
		setManualPromoApplied(false);
	};

	const refreshDiscount = (paymentMethod?: string) => {
		setAppliedDiscount(null);
		setManualPromoApplied(false);
		setAutoApplyDismissed(false);
		setPromoCode("");
		setPromoError(null);
		// Re-trigger auto-apply with new payment method
		const cartTotal = getCartTotal();
		if (cartItems.length > 0 && cartTotal > 0) {
			calculateDiscount(cartItems, cartTotal, undefined, paymentMethod);
		}
	};

	const addDirect = (item: CartItem) => {
		setCartItems(prev => [...prev, item]);
	};

	// Auto-apply discounts when cart changes
	useEffect(() => {
		const cartTotal = getCartTotal();
		if (cartItems.length > 0 && cartTotal > 0) {
			if (manualPromoApplied) {
				calculateDiscount(cartItems, cartTotal, promoCode.trim());
			} else if (!autoApplyDismissed) {
				calculateDiscount(cartItems, cartTotal);
			}
		} else if (cartItems.length === 0) {
			setAppliedDiscount(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartItems, calculateDiscount, manualPromoApplied, autoApplyDismissed]);

	// Calculate taxes when cart or discount changes
	useEffect(() => {
		const discountedTotal = getDiscountedTotal();

		if (discountedTotal > 0) {
			const calculateTaxes = async () => {
				setIsCalculatingTaxes(true);
				try {
					const response = await apiFetch('/v1/taxes/calculate', {
						method: 'POST',
						body: JSON.stringify({
							amount: discountedTotal,
							transactionType: 'order'
						})
					});

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const result = await response.json();
					if (result.success) {
						setTaxCalculation(result.data);
					} else {
						throw new Error(result.message || 'Tax calculation failed');
					}
				} catch (error) {
					console.error('Failed to calculate taxes:', error);
					setTaxCalculation({
						subtotal: discountedTotal,
						fees: [],
						total: discountedTotal
					});
				} finally {
					setIsCalculatingTaxes(false);
				}
			};
			calculateTaxes();
		} else {
			setTaxCalculation(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartItems, appliedDiscount]);

	const saveOrderToDatabase = async (
		orderData: any,
		items: CartItem[],
		taxCalc: TaxCalculationResult | null,
		discount: ApplicableDiscount | null,
		paymentMethod?: SelectablePaymentMethod
	) => {
		try {
			if (!sechPrice) {
				throw new Error('No SECH price available for order recording');
			}

			const originalSubtotal = items.reduce((sum, item) => sum + (item.unitPriceUsd * item.quantity), 0);
			const subtotal = originalSubtotal.toFixed(2);
			let taxAmount = null;
			let taxBreakdown = null;

			if (taxCalc) {
				const totalTaxUSD = taxCalc.fees.reduce((sum, fee) => sum + fee.amount, 0);
				taxAmount = totalTaxUSD.toFixed(2);
				taxBreakdown = taxCalc.fees.map(fee => ({
					rate: fee.rate,
					type: fee.type,
					amount: Number((fee.amount / sechPrice).toFixed(8)),
					amountUSD: fee.amount,
					description: fee.description
				}));
			}

			let discountId = null;
			let discountCode = null;
			let discountAmount = null;
			let discountDetails = null;

			if (discount) {
				discountId = discount.discountId;
				discountCode = discount.promoCode;
				discountAmount = discount.calculatedAmount.toFixed(2);
				discountDetails = {
					discountId: discount.discountId,
					discountName: discount.discountName,
					discountType: discount.discountType,
					discountValue: discount.discountValue,
					appliedAmount: discount.calculatedAmount,
					eligibleSubtotal: discount.eligibleSubtotal,
				};
			}

			const dbOrder: Record<string, any> = {
				locationId: orderData?.locationId,
				locationDetails: orderData?.locationDetails || null,
				locationName: orderData?.locationName,
				status: 'pending',
				notes: orderData?.notes || null,
				totalUsd: orderData.totalUsd,
				subtotal: subtotal,
				taxAmount: taxAmount,
				taxBreakdown: taxBreakdown,
				discountId: discountId,
				discountCode: discountCode,
				discountAmount: discountAmount,
				discountDetails: discountDetails,
				totalSech: orderData.totalSech.toString(),
				paymentHash: orderData.transactionHash,
				sechExchangeRate: sechPrice.toFixed(8),
				user: orderData?.user,
			};

			const dbOrderItems = items.map(item => ({
				productId: item.type === 'product' ? item.productId : null,
				customDrink: item.type === 'custom-drink' ? item.customDrink : null,
				quantity: item.quantity,
				unitPriceUsd: item.unitPriceUsd.toFixed(2),
				unitPriceSech: item.unitPriceSech.toString(),
				selectedModifiers: item.selectedModifiers || null
			}));

			const requestBody = {
				order: dbOrder,
				items: dbOrderItems,
				customerEmail: orderData.sechUser?.email || null
			};

			// Guarantee the paid order is recorded under the SAME authenticated
			// customer identity that the charge used. apiFetch attaches the Firebase
			// Bearer token, but getFirebaseAuthToken() resolves to null whenever
			// auth.currentUser is momentarily unavailable — in that case apiFetch would
			// send "Authorization: Bearer null", the API rejects it as unauthenticated,
			// and the paid order is silently dropped (this is the failure mode behind the
			// paid-but-missing pos_orders rows). Fail loudly with an auth error instead so
			// the post-charge handler surfaces it for the customer and logs it for us —
			// we never POST the order without a valid Bearer token.
			const authToken = await getFirebaseAuthToken();
			if (!authToken) {
				throw new Error('Authentication required to record order: no Firebase session token available.');
			}

			const response = await apiFetch('/v1/pos/orders', {
				withCredentials: true,
				method: 'POST',
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				const errorData = await response.text();
				throw new Error(`Failed to create order: ${errorData}`);
			}

			return await response.json();
		} catch (error: any) {
			// Don't toast here — the caller (requestPayment) decides how to surface this,
			// because the appropriate UX depends on whether the upstream charge succeeded.
			throw error;
		}
	};

	const requestPayment = async (orderId?: string, userData?: UserData, notes?: string, paymentMethod?: SelectablePaymentMethod) => {
		// Block re-entry before any other work — guards against the rapid double-tap
		// race that the state-based isPaymentProcessing check below can't catch.
		if (isSubmittingRef.current) return;

		if (!sechUser) {
			toast({
				title: "SECH wallet required",
				description: "Please connect your SECH wallet to complete the purchase.",
				variant: "destructive",
			});
			return;
		}

		if (cartItems.length === 0) {
			toast({
				title: "Cart is empty",
				description: "Add items to cart before checkout",
				variant: "destructive",
			});
			return;
		}

		if (isPaymentProcessing || isCalculatingTaxes) return;

		if (!taxCalculation) {
			toast({
				title: "Tax calculation pending",
				description: "Please wait for tax calculation to complete",
				variant: "destructive",
			});
			return;
		}

		if (!sechPrice && (!paymentMethod || paymentMethod.type === 'TOKEN')) {
			toast({
				title: "Price unavailable",
				description: "Unable to get current SECH price",
				variant: "destructive",
			});
			return;
		}

		const totalWithTaxes = getCartTotalWithTaxes();
		const totalSech = totalWithTaxes / sechPrice;

		const taxBreakdown = taxCalculation.fees.map(fee => ({
			rate: fee.rate,
			type: fee.type,
			amount: Number((fee.amount / sechPrice).toFixed(8)),
			amountUSD: fee.amount,
			description: fee.description
		}));

		const discountedSubtotal = getDiscountedTotal();
		const discountedSubtotalSech = discountedSubtotal / sechPrice;
		const originalSubtotal = getCartTotal();
		const originalSubtotalSech = originalSubtotal / sechPrice;
		const taxAmountUsd = taxCalculation.fees.reduce((sum, fee) => sum + fee.amount, 0);
		const taxAmountSech = taxAmountUsd / sechPrice;
		// Derive the discount amount straight from the cart numbers so the value sent to
		// the BE is always coherent with the totals we're sending — even if appliedDiscount
		// state was cleared by a payment-method toggle while taxCalculation still reflects
		// the discount. BE rejects a total below subtotal+tax unless discountAmount matches.
		const derivedDiscountUsd = Math.max(0, originalSubtotal - discountedSubtotal);

		userRef.current = userData || null;
		notesRef.current = notes || null;
		setLastPaymentError(null);
		isSubmittingRef.current = true;
		setIsPaymentProcessing(true);

		toast({
			title: "Processing payment...",
			description: paymentMethod?.type === 'CARD' ? "Processing credit card payment..." : "Please confirm the payment in your SECH wallet",
		});

		try {
			// Call the parent app's executePayment directly (no postMessage).
			// Send the pre-discount subtotal as `subtotal` — the BE applies the discount itself.
			const paymentResult = await executePayment({
				total: { sech: totalSech, usd: totalWithTaxes },
				subtotal: { sech: originalSubtotalSech, usd: originalSubtotal },
				discountedSubtotal: { sech: discountedSubtotalSech, usd: discountedSubtotal },
				appliedDiscount: appliedDiscount || null,
				derivedDiscountUsd,
				tax_amount: { sech: taxAmountSech, usd: taxAmountUsd },
				taxBreakdown,
				items: cartItems.map(item => ({
					id: item.id,
					productId: item.productId,
					name: item.product?.name || item.customDrink?.name,
					quantity: item.quantity,
					unitPriceUsd: item.unitPriceUsd,
					unitPriceSech: item.unitPriceSech,
					selectedModifiers: item.selectedModifiers || null,
				})),
				location,
				user: userData,
				...(paymentMethod && { paymentMethodType: paymentMethod.type, ...(paymentMethod.type === 'CARD' && { paymentMethodId: paymentMethod.id }) })
			});

			if (paymentResult.success) {
				const currentItems = [...cartItems];
				const currentTaxCalc = taxCalculation;
				const currentDiscount = appliedDiscount;
				const currentTotal = currentTaxCalc ? currentTaxCalc.total : currentItems.reduce((sum, item) => sum + (item.unitPriceUsd * item.quantity), 0);
				const totalSechAmount = currentTotal / sechPrice;

				// Prefer the canonical paymentHash from the charge response so the
				// pos_orders row links to the same transactions row the charge wrote —
				// for CARD payments there is no on-chain txHash, so paymentHash is the
				// only reference. Fall back to txHash for the TOKEN (on-chain) path.
				const rawHash = ((paymentResult as any).paymentHash ?? paymentResult.txHash) as any;
				const transactionHash: string | undefined = typeof rawHash === 'object' && rawHash !== null
					? (rawHash.hash || String(rawHash))
					: rawHash;

				// Capture the payment method on the order shape so the confirmation page
				// has correct values even when the fetched /v1/pos/orders/:id response
				// drops them. Without this, isCard resolves false on a card payment and
				// the bill summary / payment-method row both render in SECH/wallet style.
				const isCardPayment = paymentMethod?.type === 'CARD';
				const isTokenPayment = !paymentMethod || paymentMethod.type === 'TOKEN';
				const cardBrand = isCardPayment && 'cardBrand' in paymentMethod ? paymentMethod.cardBrand : null;
				const cardLast4 = isCardPayment && 'lastFour' in paymentMethod ? paymentMethod.lastFour : null;

				const orderData = {
					id: Date.now(),
					orderNumber: paymentResult.orderId || undefined,
					status: 'pending',
					locationId: location?.id,
					locationName: location?.name,
					locationDetails: location?.details || null,
					notes: notesRef.current || null,
					totalUsd: currentTotal.toFixed(2),
					totalSech: totalSechAmount,
					transactionHash,
					items: currentItems,
					taxCalculation: currentTaxCalc,
					discount: currentDiscount,
					createdAt: new Date().toISOString(),
					paymentMethod: isCardPayment ? 'CARD' as const : 'TOKEN' as const,
					cardBrand,
					cardLast4,
					walletAddress: isTokenPayment ? sechUser?.walletAddress ?? null : null,
					sechUser: {
						uid: sechUser?.uid,
						email: sechUser?.email,
						displayName: sechUser?.displayName,
						walletAddress: sechUser?.walletAddress
					},
					user: userRef.current
				};

				try {
					const savedOrder = await saveOrderToDatabase(orderData, currentItems, currentTaxCalc, currentDiscount, paymentMethod);

					const finalOrder = {
						...orderData,
						id: savedOrder.id,
						orderNumber: savedOrder.orderNumber
					};

					clearCart();

					toast({
						title: "Payment successful!",
						description: `Order ${finalOrder.orderNumber} has been placed successfully.`,
					});

					if (onPaymentSuccess) {
						onPaymentSuccess(finalOrder);
					}
				} catch (dbError: any) {
					// The charge succeeded (Mobius billed, BE transactions row written) but the
					// /v1/pos/orders call did NOT return 2xx, so no pos_orders row was created.
					// We must NOT render the "Order Confirmed!" screen here: it lies to the
					// customer and then polls GET /v1/pos/orders/<fake-id> forever (404). Instead
					// surface the failure and route into the existing recovery path (the cart's
					// error banner) WITHOUT clearing the cart or confirming success.
					let detail = "";
					try {
						const parsed = JSON.parse(dbError.message.replace("Failed to create order:", "").trim());
						if (parsed.message) detail = ` ${parsed.message}`;
					} catch { /* ignore */ }
					// Log so we don't silently swallow useful debugging info.
					console.warn("Order record failed after successful charge:", dbError);

					// Stash everything needed to re-POST the order so the customer can retry the
					// record-only step (retryOrderRecord) WITHOUT re-charging. This is the key to
					// recovering without a double-charge: the charge already succeeded, so retry
					// must never call executePayment again — only /v1/pos/orders.
					recoveryRef.current = {
						orderData,
						items: currentItems,
						taxCalc: currentTaxCalc,
						discount: currentDiscount,
						paymentMethod,
					};
					setPendingOrderRecord(true);

					setLastPaymentError(
						`Your payment went through, but we couldn't record your order${detail ? `:${detail}` : "."} Tap "Retry Placing Order" to try again — you will NOT be charged a second time. If it keeps failing, show this screen to staff.`
					);

					toast({
						title: "Order not recorded",
						description: "Your payment was received but the order could not be saved. You can retry without being charged again.",
						variant: "destructive",
					});

					isSubmittingRef.current = false;
					setIsPaymentProcessing(false);
				}
			} else {
				const msg = paymentResult.error || "Payment could not be processed. Please try again.";
				setLastPaymentError(msg);
				toast({
					title: "Payment failed",
					description: msg,
					variant: "destructive",
				});
				isSubmittingRef.current = false;
				setIsPaymentProcessing(false);
			}
		} catch (err: any) {
			isSubmittingRef.current = false;
			setIsPaymentProcessing(false);
			const msg = err.message || "Payment could not be processed. Please try again.";
			setLastPaymentError(msg);
			toast({
				title: "Payment failed",
				description: msg,
				variant: "destructive",
			});
		}
	};

	// Retry ONLY the order record for a charge that already succeeded. Never re-charges:
	// it replays the stashed /v1/pos/orders POST via saveOrderToDatabase. On success the
	// customer finally reaches the confirmation screen; on failure they can retry again
	// or hand the screen to staff.
	const retryOrderRecord = async () => {
		const recovery = recoveryRef.current;
		if (!recovery || isRecoveringOrder) return;

		setIsRecoveringOrder(true);
		setLastPaymentError(null);

		try {
			const savedOrder = await saveOrderToDatabase(
				recovery.orderData,
				recovery.items,
				recovery.taxCalc,
				recovery.discount,
				recovery.paymentMethod,
			);

			const finalOrder = {
				...recovery.orderData,
				id: savedOrder.id,
				orderNumber: savedOrder.orderNumber,
			};

			recoveryRef.current = null;
			setPendingOrderRecord(false);
			clearCart();

			toast({
				title: "Order placed!",
				description: `Order ${finalOrder.orderNumber} has been recorded successfully.`,
			});

			if (onPaymentSuccess) {
				onPaymentSuccess(finalOrder);
			}
		} catch (retryError: any) {
			let detail = "";
			try {
				const parsed = JSON.parse(retryError.message.replace("Failed to create order:", "").trim());
				if (parsed.message) detail = ` ${parsed.message}`;
			} catch { /* ignore */ }
			console.warn("Order record retry failed:", retryError);

			// Keep the recovery payload so the customer can try again.
			setLastPaymentError(
				`We still couldn't record your order${detail ? `:${detail}` : "."} You were not charged again. Try once more, or show this screen to staff so they can reconcile it.`
			);
			toast({
				title: "Still couldn't record the order",
				description: "Your payment is safe and you were not charged again. Please try again or contact staff.",
				variant: "destructive",
			});
		} finally {
			setIsRecoveringOrder(false);
		}
	};

	const value: CartContextType = {
		cartItems,
		addToCart,
		removeFromCart,
		updateQuantity,
		clearCart,
		getCartTotal,
		getCartItemCount,
		sechUser,
		requestPayment,
		isPaymentProcessing,
		taxCalculation,
		isCalculatingTaxes,
		appliedDiscount,
		promoCode,
		setPromoCode,
		applyPromoCode,
		removeDiscount,
		isValidatingPromo,
		promoError,
		getDiscountedTotal,
		refreshDiscount,
		lastPaymentError,
		clearLastPaymentError,
		pendingOrderRecord,
		isRecoveringOrder,
		retryOrderRecord,
	};

	return (
		<CartContext.Provider value={value}>
			{children}
			{pending && (
				<PosConfirmModal
					open={true}
					title="Replace items?"
					description={`Your cart has items from ${cartLocation}. You can order only from one location.`}
					confirmText="Replace"
					cancelText="Cancel"
					onClose={() => setPending(null)}
					variant={'logout'}
					onConfirm={() => {
						if (!pending) return;
						const newItem = pending;
						setPending(null);
						clearCart();
						setCartLocation(newItem.itemLocation);
						addDirect(newItem);
					}}
				/>
			)}
		</CartContext.Provider>
	);
}

export function usePosCart() {
	const context = useContext(CartContext);
	if (context === undefined) {
		throw new Error("usePosCart must be used within a PosCartProvider");
	}
	return context;
}
